import type {
  ConnectorRow,
  GlobalFilters,
  IngestionRunRow,
  IntegrationOperationsView,
  QueryResult,
} from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { round, trailingMonths } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata, chartPayload, metricValue } from '../metadata';
import { ownerRef } from '../refs';

/**
 * Integration operations (ref: 07_…png).
 *
 * Secret values are never returned by any endpoint, only the authentication
 * *status*. The Finance ERP connector is down and the Notification Service is
 * paused, so this page has a genuine degraded state to render rather than a
 * uniformly green wall.
 */

export function createIntegrationRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getOperations(filters: GlobalFilters): Promise<QueryResult<IntegrationOperationsView>> {
      const scope = applyFilters(dataset, index, user, filters);

      const connectors: ConnectorRow[] = dataset.connectors.map((connector) => ({
        id: connector.id,
        integrationId: connector.integrationId,
        name: connector.name,
        category: connector.category,
        mode: connector.mode,
        connectionHealth: connector.connectionHealth,
        lastSyncAt: connector.lastSyncAt,
        objectsIngested: connector.objectsIngested,
        recordsIngested: connector.recordsIngested,
        errorQueueCount: connector.errorQueueCount,
        dataQualityPercent: connector.dataQualityPercent,
        authenticationStatus: connector.authenticationStatus,
        authenticationMethod: connector.authenticationMethod,
        nextRefreshAt: connector.nextRefreshAt,
        owner: connector.ownerPersonCode
          ? (index.peopleByCode.get(connector.ownerPersonCode)?.displayName ?? null)
          : null,
      }));

      const recentRuns: IngestionRunRow[] = dataset.ingestionRuns
        .slice()
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
        .slice(0, 20)
        .map((run) => ({
          id: run.id,
          runCode: run.runCode,
          connectorId:
            dataset.connectors.find((c) => c.integrationId === run.connectorIntegrationId)?.id ??
            '',
          fileName: run.fileName,
          actor: ownerRef(index, user, run.actorPersonCode),
          startedAt: run.startedAt,
          durationMs: run.durationMs,
          status: run.status,
          counts: run.counts,
        }));

      const totalRecords = dataset.connectors.reduce((sum, c) => sum + c.recordsIngested, 0);
      const totalObjects = dataset.connectors.reduce((sum, c) => sum + c.objectsIngested, 0);
      const errorQueue = dataset.connectors.reduce((sum, c) => sum + c.errorQueueCount, 0);
      const healthy = dataset.connectors.filter((c) => c.connectionHealth === 'Healthy').length;

      const errorTotals = new Map<string, number>();
      for (const run of dataset.ingestionRuns) {
        for (const entry of run.errorTypes) {
          errorTotals.set(entry.errorType, (errorTotals.get(entry.errorType) ?? 0) + entry.count);
        }
      }

      const months = trailingMonths(dataset.asOf, 6);
      const qualityValues = dataset.connectors
        .map((c) => c.dataQualityPercent)
        .filter((value): value is number => value !== null);
      const averageQuality =
        qualityValues.length === 0
          ? 0
          : qualityValues.reduce((sum, v) => sum + v, 0) / qualityValues.length;

      const view: IntegrationOperationsView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-070',
            label: 'Connectors',
            value: dataset.connectors.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-071',
            label: 'Healthy Connections',
            value: round(healthy / Math.max(1, dataset.connectors.length), 4),
            unit: 'percent',
            desiredDirection: 'Up',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-072',
            label: 'Records Ingested',
            value: totalRecords,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-073',
            label: 'Error Queue',
            value: errorQueue,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
            // The one KPI on this band a reader always wants to open: a count
            // of rejected rows is only actionable once you know which source
            // produced them and why.
            drilldowns: [
              {
                dimension: 'connector',
                label: 'View the error queue',
                route: `${ROUTES.adminIntegrations}?tab=errors`,
              },
            ],
          }),
          metricValue({
            dataset,
            metricId: 'MET-074',
            label: 'Average Data Quality',
            value: round(averageQuality, 4),
            unit: 'percent',
            desiredDirection: 'Up',
            confidence: 'Medium',
          }),
        ],
        connectors,
        healthByCategory: chartPayload({
          chartId: 'integration-health-by-category',
          title: 'Connection health by category',
          accessibleSummary: 'Count of connectors in each health state, grouped by category.',
          xLabel: 'Category',
          series: (['Healthy', 'Degraded', 'Down', 'Paused'] as const).map((health) => ({
            seriesId: health,
            label: health,
            points: Array.from(new Set(dataset.connectors.map((c) => c.category))).map(
              (category) => ({
                x: category,
                y: dataset.connectors.filter(
                  (c) => c.category === category && c.connectionHealth === health,
                ).length,
              }),
            ),
          })),
        }),
        healthTrend: chartPayload({
          chartId: 'integration-health-trend',
          title: 'Healthy connectors over time',
          accessibleSummary: 'Share of connectors reporting healthy, by month.',
          xLabel: 'Month',
          series: [
            {
              seriesId: 'healthy',
              label: 'Healthy',
              points: months.map((month, i) => ({
                x: month,
                y: round(Math.min(1, 0.72 + i * 0.02), 4),
              })),
            },
          ],
        }),
        ingestionOverview: {
          recordsIngested: totalRecords,
          objectsIngested: totalObjects,
          errorQueue,
          recordsOverTime: chartPayload({
            chartId: 'integration-records-over-time',
            title: 'Records ingested',
            accessibleSummary: 'Records ingested per month across all connectors.',
            xLabel: 'Month',
            series: [
              {
                seriesId: 'records',
                label: 'Records',
                points: months.map((month, i) => ({
                  x: month,
                  y: Math.round(totalRecords * (0.82 + i * 0.04)),
                })),
              },
            ],
          }),
        },
        recentRuns,
        topErrorTypes: Array.from(errorTotals.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([errorType, count]) => ({ errorType, count })),
        dataQualityTrend: chartPayload({
          chartId: 'integration-quality-trend',
          title: 'Data quality over time',
          accessibleSummary: 'Composite data quality score by month.',
          xLabel: 'Month',
          series: [
            {
              seriesId: 'quality',
              label: 'Quality',
              points: months.map((month, i) => ({
                x: month,
                y: round(Math.min(1, averageQuality - 0.05 + i * 0.012), 4),
              })),
            },
          ],
        }),
        dataQualitySummary: (
          [
            'Completeness',
            'Validity',
            'Uniqueness',
            'Consistency',
            'Timeliness',
            'Accuracy',
          ] as const
        ).map((dimension) => {
          const rules = dataset.dataQualityRules.filter((r) => r.dimension === dimension);
          return {
            dimension,
            percent:
              rules.length === 0
                ? 1
                : round(rules.filter((r) => r.outcome !== 'Failed').length / rules.length, 4),
          };
        }),
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminIntegrations,
          sources: PAGE_SOURCES.integrations,
          confidence: 'High',
          actions: [
            {
              action: 'runConnector',
              permission: 'administer',
              enabled: user.allowedModules.includes('ingestion'),
              disabledReason: user.allowedModules.includes('ingestion')
                ? undefined
                : 'Integration administration is not part of your role.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },
  };
}
